import { ref } from 'vue'

type UseAssetsLoaderOptions = {
  concurrency?: number
  idleTimeoutMs?: number
}

export function useAssetsLoader(options: UseAssetsLoaderOptions = {}) {
  const assetsReady = ref(false)
  const loaded = new Set<string>()
  const inflight = new Map<string, Promise<void>>()
  const concurrency = Math.max(1, options.concurrency ?? 4)
  const idleTimeoutMs = options.idleTimeoutMs ?? 1500

  const loadImageOnce = (src: string) => {
    if (loaded.has(src)) return Promise.resolve()
    const existing = inflight.get(src)
    if (existing) return existing

    const promise = new Promise<void>((resolve) => {
      const img = new Image()
      img.decoding = 'async'
      let settled = false
      const finalize = () => {
        if (settled) return
        settled = true
        inflight.delete(src)
        loaded.add(src)
        resolve()
      }

      img.onload = finalize
      img.onerror = finalize
      img.src = src
      if (img.complete) finalize()
    })

    inflight.set(src, promise)
    return promise
  }

  const loadWithConcurrency = async (urls: string[]) => {
    const queue = urls.slice()
    const workers = Array.from({ length: Math.min(concurrency, queue.length) }, async () => {
      while (queue.length) {
        const url = queue.shift()
        if (url) await loadImageOnce(url)
      }
    })
    await Promise.all(workers)
  }

  const ensureAssetsLoaded = async (assets: string[]) => {
    const urls = Array.from(new Set(assets.filter(Boolean)))
    if (!urls.length) {
      assetsReady.value = true
      return true
    }

    const pending = urls.filter((url) => !loaded.has(url))
    if (!pending.length) {
      assetsReady.value = true
      return true
    }

    assetsReady.value = false
    await loadWithConcurrency(pending)
    assetsReady.value = true
    return true
  }

  const warmupAssets = (assets: string[]) => {
    const schedule = () => {
      void ensureAssetsLoaded(assets)
    }
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(schedule, { timeout: idleTimeoutMs })
    } else {
      globalThis.setTimeout(schedule, 0)
    }
  }

  return {
    assetsReady,
    ensureAssetsLoaded,
    warmupAssets
  }
}
